import {
  SOURCE_ID,
  SELF_EVALUATION_ENDPOINT,
  CONSUMER_CARLEAD_PREFIX,
  MARKETING_ATTRIBUTES_ENDPOINT,
  MARKETING_FRONTEND_AUTH,
  CID_PERSIST_ENDPOINT,
} from 'config';
import { getOr } from 'lodash/fp';
import encode from 'helpers/encode';
import { LOCALE } from 'helpers/i18n';
import { COUNTRY, LANGUAGE } from 'helpers/country/getCountryByLocale';
import cmsLocaleMapping from 'helpers/cmsLocaleMapping';

const ExtraMappingUrlsByLocale = {
  'sv-se': '/v1/sweden-license-plate/extra-mapping',
};

export default {
  getAd: (id, trackingId) => ({
    method: 'GET',
    url: `/v1/classifieds-ad-search/ad/${id}`,
    query: {
      locale: LOCALE,
      trackingId,
    },
  }),

  getAdsByType: ({ meta, type, trackingId, query = {}, userSearchType }) => ({
    method: 'POST',
    url: `/v1/classifieds-ad-search/ad/search/${type}`,
    body: { meta, trackingId, userSearchType, platform: 'web' },
    query,
    withSessionId: true,
  }),

  getCarManufacturers: () => ({
    method: 'GET',
    url: '/v1/cardata/types/manufacturers',
    query: {
      locale: LOCALE,
    },
  }),

  getCarSubTypes: (query) => ({
    method: 'GET',
    url: `/v1/car-types/manufacturer/${query.manufacturer}/sub-types`,
    query,
  }),

  getCarMainTypes: (query) => ({
    method: 'GET',
    url: '/v1/car-types/main-types',
    query,
    createId: ({ method, url, query }) =>
      `${method}+${url}+${JSON.stringify(query)}`,
  }),

  getCarMainTypesV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/main-types',
    query,
    createId: ({ method, url, query }) =>
      `${method}+${url}+${JSON.stringify(query)}`,
  }),

  getCarBuildYears: (query) => ({
    method: 'GET',
    url: '/v1/car-types/built-dates',
    query,
  }),

  getCarBuildYearsV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/built-years',
    query,
  }),

  getCarBodyTypes: (query) => ({
    method: 'GET',
    url: '/v1/car-types/body-types',
    query,
  }),

  getCarBodyTypesV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/body-types',
    query,
  }),

  getCarMainTypeDetails: (query) => ({
    method: 'GET',
    url: '/v1/car-types/main-types-details',
    query,
  }),

  getCarMainTypeDetailsV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/main-types-details',
    query,
  }),

  getCarMainTypeSubs: (query) => ({
    method: 'GET',
    url: '/v1/car-types/main-types-sub',
    query,
  }),

  getCarMainTypeSubsV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/main-types-sub',
    query,
  }),

  getCarFuelTypesV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/fuel-types',
    query,
  }),

  getCarGearTypesV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/gear-types',
    query,
  }),

  getCarDoorCountsV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/door-counts',
    query,
  }),

  getCarDoorRangesV1: (query) => ({
    method: 'GET',
    url: '/v1/cardata/types/door-ranges',
    query,
  }),

  getCmsPage: (pageName, query) => ({
    method: 'GET',
    url: `/v1/cms/${SOURCE_ID}/${getOr(
      LOCALE,
      [LOCALE],
      cmsLocaleMapping,
    )}/page/${encode(pageName)}`,
    query: {
      ...query,
      active: true,
    },
  }),

  getMarketingCmsPage: (pageName, query) => ({
    method: 'GET',
    url: `/v1/cms/1/${getOr(LOCALE, [LOCALE], cmsLocaleMapping)}/page/${encode(
      pageName,
    )}`,
    query,
  }),

  getConsumerCarlead(hash) {
    const [username, password] = (MARKETING_FRONTEND_AUTH || '').split(':');

    return {
      method: 'GET',
      url: `/v1${CONSUMER_CARLEAD_PREFIX}/carlead/${hash}`,
      ...(username && password ? { auth: { username, password } } : {}),
    };
  },

  getSeoPage: (pageName) => ({
    method: 'GET',
    url: `/v1/classifieds-seo-aggregation/seo-page/${getOr(
      LOCALE,
      [LOCALE],
      cmsLocaleMapping,
    )}/${encode(pageName)}`,
  }),

  createSalesEnquiry: (body, platform) => ({
    method: 'POST',
    url: `/v1/retail-customer-interaction/sales-enquiry/${platform}`,
    body,
  }),

  createTestDriveEnquiry: (body, platform) => ({
    method: 'POST',
    url: `/v1/retail-customer-interaction/test-drive-enquiry/${platform}`,
    body,
  }),

  getGeoLocationSuggestions: (text) => ({
    method: 'GET',
    url: '/v1/geolocation/suggestion/',
    query: {
      country: COUNTRY,
      text,
      language: LANGUAGE,
    },
  }),

  getGeoLocationPlace: (placeId) => ({
    method: 'GET',
    url: '/v1/geolocation/detail/',
    query: {
      placeId,
    },
  }),

  getSeoLinks: (make = '', model = '') => ({
    method: 'GET',
    url: `/v1/classifieds-seo/links/${make}/${model}`,
    query: {
      locale: LOCALE,
    },
  }),

  publishPrivateListing: (carLeadHash) => ({
    method: 'POST',
    url: `/v1/classifieds-private-listing/private-listing/${carLeadHash}/action`,
    body: {
      action: 'publish',
    },
  }),

  getAdPreview: (carLeadHash) => ({
    method: 'GET',
    url: `${SELF_EVALUATION_ENDPOINT}/${carLeadHash}/preview`,
  }),

  getFinancingCalculation: (body) => ({
    method: 'POST',
    url: `/v1/classifieds-financing/option/calculation`,
    body,
  }),

  addAdToFavorites: (trackingId, adId) => ({
    method: 'POST',
    url: `/v1/classifieds-favorites/favorites/${trackingId}/like/${adId}`,
  }),

  removeAdFromFavorites: (trackingId, adId) => ({
    method: 'DELETE',
    url: `/v1/classifieds-favorites/favorites/${trackingId}/like/${adId}`,
  }),

  addAdToDislikes: (trackingId, adId) => ({
    method: 'POST',
    url: `/v1/classifieds-favorites/favorites/${trackingId}/dislike/${adId}`,
  }),

  removeAdFromDislikes: (trackingId, adId) => ({
    method: 'DELETE',
    url: `/v1/classifieds-favorites/favorites/${trackingId}/dislike/${adId}`,
  }),

  getCIDSessionData: (id) => ({
    method: 'GET',
    url: `${CID_PERSIST_ENDPOINT}/${id}`,
  }),

  setCIDSessionData: (id, cid) => ({
    method: 'PUT',
    url: `${CID_PERSIST_ENDPOINT}/cid`,
    body: {
      cid,
      id,
    },
  }),

  trackCid: (body) => ({
    method: 'POST',
    url: '/v1/cid-tracking/cid/raw',
    body,
  }),

  getCidData: () => ({
    method: 'GET',
    url: '/getCidData',
    local: true,
  }),

  getEmailCount: (email) => ({
    method: 'POST',
    url: '/v1/carlead/count/email',
    body: {
      email,
    },
  }),

  getMarketingAttributes(hash, referenceType = 'carlead') {
    return {
      method: 'GET',
      url: `${MARKETING_ATTRIBUTES_ENDPOINT}/${referenceType}/${hash}`,
    };
  },

  identifyVehicle: (hash, licensePlate) => ({
    method: 'POST',
    url: `/v1/self-evaluation/${hash}/vehicle-identification`,
    body: {
      licensePlate,
    },
  }),

  getSubtypeExtra: (params) => ({
    method: 'GET',
    url: `/v1/car-types/main-types-sub`,
    query: params,
  }),

  getExtraMappingValues: (licensePlate, locale) => ({
    method: 'POST',
    url: ExtraMappingUrlsByLocale[locale],
    body: {
      licensePlate,
    },
  }),

  getCarLeadObfuscation: (hash) => ({
    method: 'GET',
    url: `/v1${CONSUMER_CARLEAD_PREFIX}/carlead-obfuscation/${hash}`,
  }),
};
